/** @file Graders store module for loading and updating graders with the server. */
import { getStoreBuilder } from '@codegrade/vuex-typex';
import client from '@/client';
import * as models from '@/models';
import * as utils from '@/utils';
const storeBuilder = getStoreBuilder();
const moduleBuilder = storeBuilder.module('graders', {
    graders: {},
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export var GradersStore;
(function (GradersStore) {
    const loaders = {
        graders: {},
    };
    const commitClearAll = moduleBuilder.commit(state => {
        utils.vueSet(state, 'graders', {});
        utils.vueSet(loaders, 'graders', {});
    }, 'commitClearAll');
    /**
     * Get the function to get the graders of an assignment from the state.
     *
     * @param state The state to get the graders for.
     * @returns A closure that take an assignment id and returns the graders or null for that
     * assignment.
     */
    function getGradersClosure(state) {
        /**
         * Get the graders for the given assignment id.
         *
         * @param assignmentId The id to get the graders for.
         * @returns The graders or null if they aren't loaded yet.
         */
        return (assignmentId) => {
            const graders = state.graders[assignmentId];
            return graders ?? null;
        };
    }
    /** Get the graders for an assignment. */
    GradersStore.getGraders = moduleBuilder.read(getGradersClosure, 'getGraders');
    /** Commit the graders to the store. */
    const commitGraders = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { assignmentId, graders } = payload;
        utils.vueSet(state.graders, assignmentId, graders);
    }, 'commitGraders');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    GradersStore.updateGraderState = moduleBuilder.commit(
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (state, payload) => {
        const { assignmentId, status } = payload;
        const graders = state.graders[assignmentId];
        if (graders != null) {
            state.graders[assignmentId] = graders.map(grader => grader.setStatus(status[grader.userId]));
        }
    }, 'updateGraderState');
    /** Load the graders for a given assignment. */
    GradersStore.loadGraders = moduleBuilder.dispatch(
    /**
     * Do the loading.
     *
     * @param context The context of the current store.
     * @param payload The payload for doing the loading.
     * @returns A promise that resolves to something unknown when the loading is finished.
     */
    async (context, payload) => {
        const { assignmentId, force = false } = payload;
        if (!force && getGradersClosure(context.state)(assignmentId) != null) {
            return null;
        }
        if (force || loaders.graders[assignmentId] == null) {
            loaders.graders[assignmentId] = (async () => {
                const data = await Array.fromAsync(utils.loadAllPages(nextToken => client.p.assignment.getAllGraders({
                    assignmentId,
                    nextToken: nextToken.extract(),
                })));
                utils.vueDelete(loaders.graders, assignmentId);
                const graders = data.map(models.Grader.fromApiServerData);
                commitGraders({ assignmentId, graders });
            })();
        }
        return loaders.graders[assignmentId];
    }, 'loadGraders');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        commitClearAll();
    });
})(GradersStore || (GradersStore = {}));
